import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, AlertTriangle, HelpCircle, Clock,
  Link2, Video, Image, Music, Filter,
  X, ChevronDown, Search, Flag, Send,
  ScanEye, ExternalLink, FileVideo, Calendar,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { FilterDropdown } from "@/components/features/FilterDropdown";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VERDICT_META = {
  REAL:      { color: "text-verdict-real",      border: "border-verdict-real/30",      bg: "bg-verdict-real/10",      icon: ShieldCheck },
  FAKE:      { color: "text-verdict-fake",      border: "border-verdict-fake/30",      bg: "bg-verdict-fake/10",      icon: AlertTriangle },
  SUSPICIOUS:{ color: "text-verdict-uncertain",  border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/10", icon: AlertTriangle },
  UNCERTAIN: { color: "text-verdict-uncertain",  border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/10", icon: HelpCircle },
};

// ── Display label: risk-based (MVP Scope) ────────────────────────────────
function getDisplayLabel(verdict, confidence, t) {
  const v = (verdict || "UNCERTAIN").toUpperCase();
  const c = parseFloat(confidence) || 0;
  if (c < 60) return t("results.displayInsufficient"); // < 60% — insufficient
  if (c < 80) return t("results.displayUncertain");    // 60–79% — uncertain regardless
  if (v === "FAKE") return t("results.displayFake");   // ≥ 80% FAKE → HIGH RISK
  if (v === "REAL") return t("results.displayReal");   // ≥ 80% REAL → LOW RISK
  return t("results.displayUncertain");                // SUSPICIOUS / UNCERTAIN
}

const SORTS = [
  { key: "newest",     labelKey: "videos.sort.newest"    },
  { key: "trending",   labelKey: "videos.sort.trending"  },
  { key: "most_views", labelKey: "videos.sort.mostViews" },
  { key: "popular",    labelKey: "videos.sort.popular"   },
];

const VERDICT_FILTERS = [
  { key: null,         labelKey: "history.verdictFilter.all"       },
  { key: "FAKE",       labelKey: "history.verdictFilter.fake"      },
  { key: "REAL",       labelKey: "history.verdictFilter.real"      },
  { key: "UNCERTAIN",  labelKey: "history.verdictFilter.uncertain"  },
];

const MEDIA_TYPES = [
  { key: null,    labelKey: "history.mediaFilter.all",   Icon: Filter  },
  { key: "video", labelKey: "history.mediaFilter.video", Icon: Video   },
  { key: "image", labelKey: "history.mediaFilter.image", Icon: Image   },
  { key: "audio", labelKey: "history.mediaFilter.audio", Icon: Music   },
];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// ── Flag Dropdown (P-B.3 with reason selection) ──────────────────────────
const FLAG_REASONS = [
  { key: "misleading",    labelKey: "discover.flagReason.misleading" },
  { key: "inappropriate", labelKey: "discover.flagReason.inappropriate" },
  { key: "duplicate",     labelKey: "discover.flagReason.duplicate" },
  { key: "hoax",          labelKey: "discover.flagReason.hoax" },
  { key: "other",         labelKey: "discover.flagReason.other" },
];

function FlagDropdown({ analysisId, reported, onReport, t }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submit = () => {
    if (!reason) return;
    onReport(analysisId, reason, reason === "other" ? details : "");
    setOpen(false);
    setReason(""); setDetails("");
  };

  if (reported) {
    return (
      <span className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/40 px-1.5 py-0.5">
        <Flag className="w-3 h-3" /> {t("discover.reported")}
      </span>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={t("discover.reportBtn")}
        data-testid={`flag-btn-${analysisId}`}
        className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/50 hover:text-amber-500 hover:bg-amber-500/10 px-1.5 py-0.5 rounded-sm transition-colors"
      >
        <Flag className="w-3 h-3" /> {t("discover.reportBtn")}
      </button>
      {open && (
        <div className="absolute bottom-full right-0 mb-1 w-56 bg-card border border-border rounded-sm shadow-xl z-50">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-[10px] font-mono font-semibold text-muted-foreground uppercase tracking-wider">{t("discover.flagReasonTitle")}</p>
          </div>
          <div className="py-1">
            {FLAG_REASONS.map((r) => (
              <button
                key={r.key}
                onClick={() => setReason(r.key)}
                className={`w-full text-left text-xs px-3 py-2 hover:bg-secondary/50 transition-colors flex items-center gap-2 ${reason === r.key ? "text-brand-primary font-semibold" : ""}`}
              >
                <span className={`w-2 h-2 rounded-full border flex-shrink-0 ${reason === r.key ? "bg-brand-primary border-brand-primary" : "border-muted-foreground/40"}`} />
                {t(r.labelKey)}
              </button>
            ))}
          </div>
          {reason === "other" && (
            <div className="px-3 pb-2">
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, 200))}
                placeholder={t("discover.flagDetailsPlaceholder")}
                rows={2}
                className="w-full text-xs border border-border rounded-sm bg-background p-2 resize-none focus:outline-none focus:border-brand-primary"
              />
            </div>
          )}
          <div className="px-3 pb-2">
            <button
              onClick={submit}
              disabled={!reason}
              data-testid={`flag-submit-${analysisId}`}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-brand-primary text-primary-foreground rounded-sm py-1.5 disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              <Send className="w-3 h-3" /> {t("discover.flagSubmit")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DateRangePicker({ dateFrom, dateTo, onChange, t }) {
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom || "");
  const [localTo, setLocalTo] = useState(dateTo || "");
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const apply = () => {
    onChange(localFrom, localTo);
    setOpen(false);
  };
  const clear = () => {
    setLocalFrom(""); setLocalTo("");
    onChange("", "");
    setOpen(false);
  };

  const hasFilter = dateFrom || dateTo;
  const label = hasFilter
    ? `${dateFrom || "…"} → ${dateTo || "…"}`
    : t("discover.dateRange");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="date-range-btn"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono transition-colors border ${
          hasFilter
            ? "bg-brand-primary text-white border-brand-primary"
            : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
        }`}
      >
        <Calendar className="w-3 h-3" />
        <span>{label}</span>
        {hasFilter && (
          <X className="w-3 h-3 ml-0.5 hover:opacity-70" onClick={(e) => { e.stopPropagation(); clear(); }} />
        )}
        {!hasFilter && <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 w-72 rounded-md border border-border bg-background shadow-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("discover.dateRange")}</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("discover.dateFrom")}</label>
              <input
                type="date"
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                data-testid="date-from-input"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("discover.dateTo")}</label>
              <input
                type="date"
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                data-testid="date-to-input"
                className="w-full text-xs px-2 py-1.5 rounded-sm border border-border bg-secondary/30 text-foreground focus:outline-none focus:border-brand-primary"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} className="flex-1 h-7 rounded-sm text-xs bg-brand-primary text-white">{t("discover.applyFilter")}</Button>
            <Button size="sm" variant="ghost" onClick={clear} className="h-7 rounded-sm text-xs">{t("common.cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Discover() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [total, setTotal]       = useState(0);
  const [verdict, setVerdict]   = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [sort, setSort]         = useState("newest");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [search, setSearch]     = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [reportedIds, setReportedIds] = useState(new Set());
  const debounceRef = useRef(null);

  const handleReport = async (analysisId, reason = "other", details = "") => {
    if (reportedIds.has(analysisId)) return;
    try {
      await fetch(`${API}/public/analyses/${analysisId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason,
          details,
          reporter_user_id: user?.user_id || user?.id || "",
        }),
      });
      setReportedIds((prev) => new Set([...prev, analysisId]));
      toast.success(t("discover.reportSent", "Laporan terkirim. Terima kasih."));
    } catch {
      toast.error(t("common.error", "Terjadi kesalahan."));
    }
  };

  const fetchData = useCallback(async (p = 1, opts = {}) => {
    setLoading(true);
    try {
      const lang = i18n.language?.split("-")[0] || "id";
      const v  = "verdict"   in opts ? opts.verdict   : verdict;
      const mt = "mediaType" in opts ? opts.mediaType : mediaType;
      const s  = "sort"      in opts ? opts.sort      : sort;
      const df = "dateFrom"  in opts ? opts.dateFrom  : dateFrom;
      const dt = "dateTo"    in opts ? opts.dateTo    : dateTo;
      const sq = "search"    in opts ? opts.search    : search;
      const params = { page: p, limit: 15, sort: s, lang };
      if (v)  params.verdict    = v;
      if (mt) params.media_type = mt;
      if (df) params.date_from  = df;
      if (dt) params.date_to    = dt;
      if (sq) params.search     = sq;
      const res = await axios.get(`${API}/public/analyses`, { params });
      setAnalyses(res.data.analyses || []);
      setPage(res.data.page);
      setPages(res.data.pages);
      setTotal(res.data.total);
    } catch {
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language, verdict, mediaType, sort, dateFrom, dateTo, search]);

  useEffect(() => {
    fetchData(1);
  }, [verdict, sort, mediaType, i18n.language, fetchData]);

  const handleDateChange = (df, dt) => {
    setDateFrom(df); setDateTo(dt);
    fetchData(1, { dateFrom: df, dateTo: dt });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setSearch(q);
    fetchData(1, { search: q });
  };

  // Debounced search — fires 400ms after user stops typing
  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val.trim());
      fetchData(1, { search: val.trim() });
    }, 400);
  };

  const handlePage = (p) => {
    fetchData(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen" data-testid="discover-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <ScanEye className="w-4 h-4 text-brand-primary" />
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("discover.archive")}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                {t("discover.title")}
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                {t("discover.subtitle")}{" "}
                <span className="font-mono text-brand-primary">{total.toLocaleString()}</span> {t("discover.public")}.
              </p>
            </div>
            <Link to={user ? "/analyze" : "/register"}>
              <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white text-sm gap-2" data-testid="cta-analyze-btn">
                {t("discover.analyzeBtn")} <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Sort Tabs */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.08 }}
          className="flex items-center gap-1 border-b border-border mb-1" data-testid="sort-tabs">
          {SORTS.map(({ key, labelKey }) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-sm transition-colors border-b-2 -mb-px ${
                sort === key
                  ? "text-foreground border-brand-primary"
                  : "text-muted-foreground border-transparent hover:text-foreground hover:border-border"
              }`}
              data-testid={`sort-${key}`}
            >
              {t(labelKey)}
            </button>
          ))}
        </motion.div>

        {/* Filters row */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-3 mt-4 mb-8" data-testid="filters-row">

          {/* Left: Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-0">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder={t("discover.searchPlaceholder", "Cari judul atau deskripsi...")}
                value={searchInput}
                onChange={handleSearchInput}
                data-testid="discover-search-input"
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-sm border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand-primary"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" className="rounded-sm h-7 px-3 text-xs shrink-0" data-testid="discover-search-btn">
              {t("history.search.btn")}
            </Button>
          </form>

          {/* Right: Dropdowns + Date Range */}
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* Verdict dropdown */}
            <FilterDropdown
              options={VERDICT_FILTERS}
              value={verdict}
              onChange={(v) => { setVerdict(v); fetchData(1, { verdict: v }); }}
              t={t}
              testId="discover-verdict-dropdown"
            />

            {/* Media type dropdown */}
            <FilterDropdown
              options={MEDIA_TYPES}
              value={mediaType}
              onChange={(mt) => { setMediaType(mt); fetchData(1, { mediaType: mt }); }}
              t={t}
              testId="discover-media-dropdown"
            />

            {/* Date Range */}
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} t={t} />
          </div>
        </motion.div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="rounded-md border border-border bg-card overflow-hidden animate-pulse">
                <div className="h-40 bg-secondary/40" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-secondary/60 rounded w-3/4" />
                  <div className="h-3 bg-secondary/40 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && analyses.length === 0 && (
          <div className="text-center py-24 space-y-4" data-testid="discover-empty">
            <FileVideo className="w-12 h-12 text-muted-foreground/30 mx-auto" />
            <p className="font-heading text-xl font-semibold">{t("discover.empty.title")}</p>
            <p className="text-sm text-muted-foreground">{t("discover.empty.text")}</p>
            <Link to="/register">
              <Button className="rounded-md bg-brand-primary text-white mt-2">{t("discover.empty.cta")}</Button>
            </Link>
          </div>
        )}

        {/* Grid */}
        {!loading && analyses.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${verdict}-${sort}-${mediaType}-${page}`}
              variants={container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              data-testid="discover-grid"
            >
              {analyses.map((analysis) => {
                const meta = VERDICT_META[analysis.verdict?.toUpperCase()] || VERDICT_META.UNCERTAIN;
                const Icon = meta.icon;
                const displayLabel = getDisplayLabel(analysis.verdict, analysis.confidence, t);
                const shareLink = analysis.share_id ? `/share/${analysis.share_id}` : `/share/${analysis.id}`;
                const locale = i18n.language === "id" ? "id-ID" : "en-US";
                const date = analysis.created_at
                  ? new Date(analysis.created_at).toLocaleDateString(locale, {
                      day: "numeric", month: "short", year: "numeric"
                    })
                  : "—";
                // Use media_type (image/video/audio) not video_type (which is "upload" for uploads)
                const resolvedType = analysis.media_type || analysis.video_type;
                const typeIcon = resolvedType === "image" ? Image
                  : resolvedType === "audio" ? Music : Video;
                const TypeIcon = typeIcon;

                // P-A.2: anonymized secondary subtitle (no raw filename in public view)
                const typeLabel = resolvedType === "image"
                  ? t("discover.media.image")
                  : resolvedType === "audio" ? t("discover.media.audio") : t("discover.media.video");
                const anonymousSubtitle = `${typeLabel} • ${date}`;

                return (
                  <motion.div
                    key={analysis.id}
                    variants={item}
                    className="group rounded-md border border-border bg-card hover:border-brand-primary/30 transition-all overflow-hidden"
                    data-testid={`discover-card-${analysis.id}`}
                  >
                    {/* Thumbnail */}
                    <Link to={shareLink} className="block relative overflow-hidden">
                      {analysis.thumbnail ? (
                        <div className="relative h-40 overflow-hidden bg-secondary/30">
                          <img
                            src={analysis.thumbnail}
                            alt={anonymousSubtitle}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className={`absolute bottom-2 left-3 flex items-center gap-1.5 text-xs font-mono font-semibold px-2 py-0.5 rounded-sm border ${meta.color} ${meta.border} bg-black/60`}>
                            <Icon className="w-3 h-3" />
                            {displayLabel}
                          </div>
                          <span className={`absolute bottom-2 right-3 text-base font-heading font-bold ${meta.color}`}>
                            {Math.round(analysis.confidence)}%
                          </span>
                          <div className="absolute top-2 right-2 p-1 rounded-sm bg-black/50">
                            <TypeIcon className="w-3 h-3 text-white/70" />
                          </div>
                        </div>
                      ) : (
                        <div className={`relative h-40 flex items-center justify-center ${meta.bg} border-b ${meta.border}`}>
                          <div className="text-center">
                            <Icon className={`w-10 h-10 mx-auto mb-2 ${meta.color} opacity-60`} />
                            <div className={`text-xs font-mono font-bold ${meta.color}`}>{displayLabel}</div>
                            <div className={`text-lg font-heading font-bold ${meta.color}`}>
                              {Math.round(analysis.confidence)}%
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 p-1 rounded-sm bg-black/20">
                            <TypeIcon className={`w-3 h-3 ${meta.color} opacity-60`} />
                          </div>
                        </div>
                      )}
                    </Link>

                    {/* Card body */}
                    <div className="p-4 flex flex-col gap-2">
                      <Link to={shareLink} className="block group">
                        {/* Title — 2 lines */}
                        <h3 className="text-sm font-semibold line-clamp-2 group-hover:text-brand-primary transition-colors leading-snug" data-testid={`discover-title-${analysis.id}`}>
                          {analysis.content_title || anonymousSubtitle}
                        </h3>
                      </Link>

                      {/* Excerpt — max 120 chars, replaces description */}
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        {analysis.excerpt || analysis.summary || ""}
                      </p>

                      {/* Disclaimer */}
                      <p className="text-[10px] text-muted-foreground/50 italic leading-relaxed">
                        {t("results.verdictDisclaimer")}
                      </p>

                      {/* Metadata row 1: media type (left) + uploader (right) */}
                      <div className="flex items-center justify-between pt-1 border-t border-border/50">
                        <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/60">
                          <TypeIcon className="w-3 h-3" />
                          {typeLabel}
                        </div>
                        <span className="text-[10px] font-mono text-muted-foreground/70 truncate max-w-[120px] text-right">
                          {analysis.user_display_name || "Veriksa"}
                        </span>
                      </div>

                      {/* Metadata row 2: date + flag dropdown */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/60">
                          <Clock className="w-3 h-3" />
                          {date}
                        </div>
                        <FlagDropdown
                          analysisId={analysis.id}
                          reported={reportedIds.has(analysis.id)}
                          onReport={handleReport}
                          t={t}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
            <Button variant="outline" size="sm" onClick={() => handlePage(page - 1)}
              disabled={page <= 1} className="rounded-sm text-xs">
              &larr; {t("discover.prev")}
            </Button>
            <span className="text-xs font-mono text-muted-foreground px-2">{page} / {pages}</span>
            <Button variant="outline" size="sm" onClick={() => handlePage(page + 1)}
              disabled={page >= pages} className="rounded-sm text-xs">
              {t("discover.next")} &rarr;
            </Button>
          </div>
        )}

        {/* CTA banner — show only for guest users */}
        {!user && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mt-16 rounded-md border border-brand-primary/20 bg-brand-primary/5 p-8 text-center">
            <h2 className="font-heading text-2xl font-bold mb-2">{t("discover.cta.title")}</h2>
            <p className="text-muted-foreground text-sm mb-5 max-w-md mx-auto">{t("discover.cta.desc")}</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/register">
                <Button className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white">{t("discover.cta.btn")}</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="rounded-md">{t("nav.pricing")}</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
